import { objectToFormData } from "api/utils";
import axios, { AxiosError, AxiosResponse } from "axios";
import { BaseRequest } from "components/types/Api";
import { ApiSimpleResponse, BaseResponse, ErrorResponse } from "../../components/types";
import { StatusCode } from "../const"

const resolveFunc = <TResponse extends BaseResponse>(r: AxiosResponse<TResponse>): ApiSimpleResponse<TResponse> => {
    const response = r.data;
    return { isSuccess: response.errorCode === 0, response }
}

const rejectFunc = (e: AxiosError<ErrorResponse>) => {
    const response = e.response;
    
    if (!response || response?.status >= StatusCode.INTERNAL_SERVER_ERROR) {
        return { isSuccess: false, error: "An unexpected error occurred, retry later" }
    }

    if (response?.status === StatusCode.NOT_AUTHORIZED) {
        return { isSuccess: false, error: `${StatusCode.NOT_AUTHORIZED}` }
    }

    if (response?.status === StatusCode.BAD_REQUEST || response?.status === StatusCode.NOT_FOUND) {
        return { isSuccess: false, error: response.data.errorMessage }
    }

    const errorDetails = response?.data?.details;
    if (!!errorDetails?.errors) {
        const [firstError] = Object.keys(errorDetails.errors)

        return { isSuccess: false, error: errorDetails.errors[firstError][0] }
    }
    return { isSuccess: false, error: response?.data?.details?.detail }
}

/**
* Creates a post call to the endpoint.
*
* @param TRequest - The request type
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @param request - The body of the request
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const postCall =
    async <TRequest, TResponse extends BaseResponse>(endpoint: string, request: TRequest): Promise<ApiSimpleResponse<TResponse>> => {
        return await axios
            .post(endpoint, request)
            .then((r: AxiosResponse<TResponse>) => resolveFunc<TResponse>(r))
            .catch(rejectFunc);
    }

/**
* Creates a post call to the endpoint.
*
* @param TRequest - The request type
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @param request - The body of the request
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const patchCall =
async <TRequest, TResponse extends BaseResponse>(endpoint: string, request: TRequest): Promise<ApiSimpleResponse<TResponse>> => {
    return await axios
        .patch(endpoint, request)
        .then((r: AxiosResponse<TResponse>) => resolveFunc<TResponse>(r))
        .catch(rejectFunc);
}

/**
* Creates a post call as form data to the endpoint.
*
* @param TRequest - The request type
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @param request - The body of the request
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const postFormDataCall =
    async <TRequest extends BaseRequest, TResponse extends BaseResponse>(endpoint: string, request: TRequest): Promise<ApiSimpleResponse<TResponse>> => {
        const formData = objectToFormData({ ...request })
        return await postCall<FormData, TResponse>(endpoint, formData);
    }

/**
* Creates a put call to the endpoint.
*
* @param TRequest - The request type
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @param request - The body of the request
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const putCall =
    async <TRequest, TResponse extends BaseResponse>(endpoint: string, request: TRequest): Promise<ApiSimpleResponse<TResponse>> => {
        return await axios
            .put(endpoint, request)
            .then((r: AxiosResponse<TResponse>) => resolveFunc<TResponse>(r))
            .catch(rejectFunc);
    }

/**
* Creates a post call as form data to the endpoint.
*
* @param TRequest - The request type
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @param request - The body of the request
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const putFormDataCall =
    async <TRequest extends BaseRequest, TResponse extends BaseResponse>(endpoint: string, request: TRequest): Promise<ApiSimpleResponse<TResponse>> => {
        const formData = objectToFormData({ ...request })
        return await putCall<FormData, TResponse>(endpoint, formData);
    }

/**
* Creates a get call to the endpoint.
*
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const getCall =
    async <TResponse extends BaseResponse>(endpoint: string): Promise<ApiSimpleResponse<TResponse>> => {
        return await axios
            .get(endpoint)
            .then((r: AxiosResponse<TResponse>) => resolveFunc<TResponse>(r))
            .catch(rejectFunc);
    }

/**
* Creates a get call trimming slashes (with regex) to the endpoint and adding an id.
*
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @param id - The id
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const getByIdCall = async <TResponse extends BaseResponse>(endpoint: string, id: string | number): Promise<ApiSimpleResponse<TResponse>> => {
    return await
        getCall<TResponse>(`${endpoint.replace(/\/+$/g, '')}/${id}`);
}

/**
* Creates a delete call to the endpoint.
*
* @param TResponse - The expected response type
* @param endpoint - The endpoint
* @returns An {@link ApiSimpleResponse} of TResponse
*/
export const deleteCall =
    async <TResponse extends BaseResponse>(endpoint: string): Promise<ApiSimpleResponse<TResponse>> => {
        return await axios
            .delete(endpoint)
            .then((r: AxiosResponse<TResponse>) => resolveFunc<TResponse>(r))
            .catch(rejectFunc);
    }
